import { useCallback, useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DoorOpen, Loader2, Plus, Pencil, Trash2, Camera, CalendarClock, History, Link2,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  ["notice_recorded", "Notice recorded"], ["visit_recorded", "Visit recorded"],
  ["entry_occurred", "User recorded that entry occurred"], ["rescheduled", "Rescheduled"],
  ["cancelled", "Cancelled"], ["no_outcome_recorded", "No outcome recorded"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map(([k, v]) => [k, v]));
const METHODS = [["", "—"], ["email", "Email"], ["text", "Text"], ["call", "Call"], ["in_person", "In person"], ["letter", "Letter"], ["portal", "Portal"], ["none", "No notice recorded"], ["other", "Other"]];
const METHOD_LABEL = Object.fromEntries(METHODS.map(([k, v]) => [k, v]));
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function EntryLog({ propertyId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/properties/${propertyId}/entry-log`); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open the entry log."); onClose?.(); }
    finally { setLoading(false); }
  }, [propertyId, onClose]);

  useEffect(() => { load(); }, [load]);

  const readOnly = data?.read_only;

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/entry-log/${id}`); toast.success("Event deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="entry-log" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DoorOpen size={18} className="text-brand" /> Landlord entry log</DialogTitle>
          <DialogDescription>Your factual record of notices and property visits.</DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex items-center justify-center py-10 text-[#64748B]" data-testid="el-loading"><Loader2 className="animate-spin" size={20} /></div>
        ) : (
          <div className="space-y-4 text-sm text-[#0F172A]">
            {!readOnly && (
              <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="el-add-btn" className="rounded-lg h-9 w-full bg-brand hover:bg-brand-hover text-white text-xs">
                <Plus size={14} className="mr-1.5" /> Record a notice or visit
              </Button>
            )}

            <div data-testid="el-events">
              {data.events.length === 0 && <p className="text-xs text-[#94A3B8]">Nothing recorded yet.</p>}
              <div className="space-y-2">
                {data.events.map((ev) => (
                  <div key={ev.event_id} data-testid={`el-event-${ev.event_id}`} className="rounded-lg border border-[#E2E8F0] p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                          <span className="rounded-md bg-[#EEF2FF] text-brand px-1.5 py-0.5 text-[10px]" data-testid={`el-status-${ev.event_id}`}>{ev.status_label}</span>
                          {ev.purpose && <span className="text-[#334155] font-normal">{ev.purpose}</span>}
                        </p>
                        <p className="text-[11px] text-[#94A3B8] flex items-center gap-1"><CalendarClock size={11} /> {fmtDate(ev.proposed_date)}{ev.proposed_time ? ` · ${ev.proposed_time}` : ""}{ev.notice_method ? ` · notice: ${METHOD_LABEL[ev.notice_method] || ev.notice_method}` : ""}</p>
                        {ev.visit_occurred != null && <p className="text-[11px] text-[#475569]">Visit occurred: {ev.visit_occurred ? "yes (user-recorded)" : "no (user-recorded)"}{ev.attendees ? ` · ${ev.attendees}` : ""}</p>}
                        {(ev.arrival_time || ev.departure_time) && <p className="text-[11px] text-[#94A3B8]">User-entered time: {ev.arrival_time || "?"} – {ev.departure_time || "?"}</p>}
                        {ev.note && <p className="text-xs text-[#64748B] mt-0.5">{ev.note}</p>}
                        {ev.linked_comm && <p className="text-[10px] text-sky-600 mt-0.5" data-testid={`el-linked-comm-${ev.event_id}`}><Link2 size={9} className="inline" /> Linked communication: {ev.linked_comm.summary?.slice(0, 50)}</p>}
                        {ev.linked_issue && <p className="text-[10px] text-violet-600 mt-0.5" data-testid={`el-linked-issue-${ev.event_id}`}><Link2 size={9} className="inline" /> Linked repair case: {ev.linked_issue.title}</p>}
                        {(ev.history || []).length > 0 && <p className="text-[10px] text-[#94A3B8] mt-0.5" data-testid={`el-history-${ev.event_id}`}><History size={9} className="inline" /> Corrected — earlier history kept</p>}
                        {[...(ev.before_photos || []), ...(ev.after_photos || [])].length > 0 && (
                          <div className="mt-1.5 flex gap-1.5">
                            {[...(ev.before_photos || []), ...(ev.after_photos || [])].map((p) => (
                              <a key={p.photo_id} href={fileUrl(p.storage_path)} target="_blank" rel="noopener noreferrer" className="h-11 w-11 rounded-lg overflow-hidden border border-[#E2E8F0]">
                                <img src={fileUrl(p.storage_path)} alt="" className="h-full w-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <div className="flex gap-0.5 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => { setEditing(ev); setFormOpen(true); }} data-testid={`el-edit-${ev.event_id}`} className="h-7 w-7 p-0 rounded-lg text-[#64748B]"><Pencil size={13} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(ev.event_id)} data-testid={`el-delete-${ev.event_id}`} className="h-7 w-7 p-0 rounded-lg text-[#FCA5A5]"><Trash2 size={13} /></Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[11px] text-[#94A3B8] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2" data-testid="el-disclaimer">{data.disclaimer}</p>
          </div>
        )}

        {formOpen && (
          <EventForm propertyId={propertyId} event={editing}
            onClose={() => { setFormOpen(false); load(); }} onSaved={() => { setFormOpen(false); load(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EventForm({ propertyId, event, onClose, onSaved }) {
  const isEdit = !!event;
  const [status, setStatus] = useState(event?.status || "notice_recorded");
  const [method, setMethod] = useState(event?.notice_method || "");
  const [date, setDate] = useState(event?.proposed_date?.slice(0, 10) || "");
  const [time, setTime] = useState(event?.proposed_time || "");
  const [purpose, setPurpose] = useState(event?.purpose || "");
  const [visit, setVisit] = useState(event?.visit_occurred == null ? "" : (event.visit_occurred ? "yes" : "no"));
  const [attendees, setAttendees] = useState(event?.attendees || "");
  const [arrival, setArrival] = useState(event?.arrival_time || "");
  const [departure, setDeparture] = useState(event?.departure_time || "");
  const [note, setNote] = useState(event?.note || "");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(event?.event_id || null);
  const [before, setBefore] = useState(event?.before_photos || []);
  const [after, setAfter] = useState(event?.after_photos || []);
  const beforeRef = useRef(null); const afterRef = useRef(null);

  const body = () => ({
    status, notice_method: method || null, notice_received: !!method && method !== "none",
    proposed_date: date || null, proposed_time: time || null, purpose,
    visit_occurred: visit === "" ? null : visit === "yes",
    attendees, arrival_time: arrival, departure_time: departure, note,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit || savedId) {
        const id = isEdit ? event.event_id : savedId;
        await api.patch(`/entry-log/${id}`, body());
        toast.success("Updated");
        if (isEdit) onSaved();
      } else {
        const { data } = await api.post(`/properties/${propertyId}/entry-log`, body());
        setSavedId(data.event_id); toast.success("Recorded — attach photos below or close.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const upload = async (slot, file) => {
    if (!savedId || !file) return;
    try {
      const form = new FormData(); form.append("slot", slot); form.append("file", file);
      const { data } = await api.post(`/properties/${propertyId}/entry-log/${savedId}/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setBefore(data.before_photos || []); setAfter(data.after_photos || []);
      toast.success("Evidence attached");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't attach."); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="el-event-form" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Correct event" : "Record a notice or visit"}</DialogTitle>
          <DialogDescription>Only facts you record. TenantSeal doesn't judge notice, authorization, or legality.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <select data-testid="el-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-2 text-sm">
                {STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Notice method</Label>
              <select data-testid="el-method-select" value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-2 text-sm">
                {METHODS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Proposed date</Label><Input data-testid="el-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
            <div><Label className="text-xs">Time (as stated)</Label><Input data-testid="el-time-input" value={time} onChange={(e) => setTime(e.target.value)} placeholder="e.g. 10:00 AM" className="mt-1 h-10 rounded-xl" /></div>
          </div>
          <div><Label className="text-xs">Stated purpose</Label><Input data-testid="el-purpose-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Inspection" className="mt-1 h-10 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Did the visit occur?</Label>
              <select data-testid="el-visit-select" value={visit} onChange={(e) => setVisit(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-2 text-sm">
                <option value="">No outcome recorded</option><option value="yes">Yes (user-recorded)</option><option value="no">No (user-recorded)</option>
              </select>
            </div>
            <div><Label className="text-xs">Who attended (you say)</Label><Input data-testid="el-attendees-input" value={attendees} onChange={(e) => setAttendees(e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Arrival (user-entered)</Label><Input data-testid="el-arrival-input" value={arrival} onChange={(e) => setArrival(e.target.value)} placeholder="e.g. 10:05" className="mt-1 h-10 rounded-xl" /></div>
            <div><Label className="text-xs">Departure (user-entered)</Label><Input data-testid="el-departure-input" value={departure} onChange={(e) => setDeparture(e.target.value)} placeholder="e.g. 10:40" className="mt-1 h-10 rounded-xl" /></div>
          </div>
          <div><Label className="text-xs">Note (optional)</Label><Textarea data-testid="el-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 rounded-xl" /></div>

          {savedId && (
            <div className="rounded-xl border border-[#E2E8F0] p-2.5" data-testid="el-photo-area">
              <p className="text-xs font-medium mb-1.5">Before / after evidence (optional)</p>
              <div className="flex gap-2">
                <input ref={beforeRef} type="file" accept="image/*" className="hidden" data-testid="el-before-input" onChange={(e) => { upload("before", e.target.files?.[0]); e.target.value = ""; }} />
                <input ref={afterRef} type="file" accept="image/*" className="hidden" data-testid="el-after-input" onChange={(e) => { upload("after", e.target.files?.[0]); e.target.value = ""; }} />
                <Button size="sm" variant="outline" onClick={() => beforeRef.current?.click()} data-testid="el-before-btn" className="rounded-lg text-xs"><Camera size={13} className="mr-1" /> Before ({before.length})</Button>
                <Button size="sm" variant="outline" onClick={() => afterRef.current?.click()} data-testid="el-after-btn" className="rounded-lg text-xs"><Camera size={13} className="mr-1" /> After ({after.length})</Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="el-close">{savedId && !isEdit ? "Done" : "Cancel"}</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="el-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : (isEdit ? "Save correction" : (savedId ? "Update" : "Record event"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
